import * as React from "react";
import { FC, useCallback, useState } from "react";

import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import { MAX_RATES_LIMIT } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { type Path, useFieldArray, type UseFormReturn } from "react-hook-form";

import { AddOnCard } from "./AddOnCard/AddOnCard";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { AddDivider } from "@/features/LiveFormEditor/Steps/components/AddDivider";
import { buildDefaultAddOn } from "@/features/LiveFormEditor/Steps/StepAddOns/helpers/buildDefaultAddOn";
import { StepAddOnsFormData } from "@/features/LiveFormEditor/Steps/StepAddOns/types";
import { cloneRateWithNewIds } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/helpers/cloneRate";
import { getInheritedTaxIds } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/helpers/rateTaxes";
import { RateOptionHighlightProvider } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/RateOptionHighlight/RateOptionHighlightContext";
import { ControlledRatesExpansionProvider } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/RatesExpansion/RatesExpansionContext";
import { RateData } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/types";
import { Add } from "@/icons/outlined";

export interface AddOnsListProps {
  form: UseFormReturn<StepAddOnsFormData>;
}

export const AddOnsList: FC<AddOnsListProps> = ({ form }) => {
  const { t } = useTranslate();
  const { editOrCreateMode } = useLiveFormEditorContext<"ticketing">();
  const { isSmallScreen } = useMediaQuery();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const { enqueueSnackbar } = useSnackbar();

  const { control, setFocus } = form;

  const {
    fields: addOns,
    append,
    remove,
    move,
    insert,
  } = useFieldArray<StepAddOnsFormData>({
    control,
    name: "rates",
    keyName: "key" as "id",
  });

  const [expandedRateId, setExpandedRateId] = useState<string | null>(null);
  const toggleExpanded = useCallback((id: string) => {
    setExpandedRateId((prev) => (prev === id ? null : id));
  }, []);

  const scrollAndFocusAddOn = useCallback(
    (index: number) => {
      window.setTimeout(() => {
        requestAnimationFrame(() => {
          const element = document.querySelector<HTMLElement>(`[data-index="${index}"]`);
          element?.scrollIntoView({ behavior: "smooth", block: "start" });
          const fieldName = `rates.${index}.name` as Path<StepAddOnsFormData>;
          const input = document.querySelector<HTMLElement>(`[name="${fieldName}"]`);
          if (input) {
            input.focus({ preventScroll: true });
          } else {
            setFocus(fieldName);
          }
        });
      }, 320);
    },
    [setFocus]
  );

  const addAddOn = useCallback(
    (position?: number) => {
      const currentLength = (form.getValues("rates") ?? []).length;
      if (currentLength >= MAX_RATES_LIMIT) {
        enqueueSnackbar(t("dashboard", "ticketing.maxRatesReached", { count: MAX_RATES_LIMIT }), { vibe: "danger" });
        return;
      }
      logUserActionEvent(AmplitudeEvents.FormAddOnAdded);
      const newIndex = typeof position === "number" ? position : currentLength;
      const previousAddOn = (form.getValues("rates") ?? [])[newIndex - 1];
      const newAddOn = { ...buildDefaultAddOn(), taxIds: getInheritedTaxIds(previousAddOn) };
      if (typeof position === "number") {
        insert(position, [newAddOn]);
      } else {
        append([newAddOn]);
      }
      scrollAndFocusAddOn(newIndex);
    },
    [append, enqueueSnackbar, form, insert, logUserActionEvent, scrollAndFocusAddOn, t]
  );

  const deleteAddOn = useCallback(
    (index: number) => {
      const formAddOns = form.getValues("rates") ?? [];
      const id = formAddOns[index]?.id;
      if (id) {
        form.setValue("ratesToDelete", [...(form.getValues("ratesToDelete") ?? []), id]);
        setExpandedRateId((prev) => (prev === id ? null : prev));
      }
      remove(index);
    },
    [form, remove]
  );

  const duplicateAddOn = useCallback(
    (index: number) => {
      const addOnToClone = (form.getValues("rates") ?? [])[index] as RateData | undefined;
      if (!addOnToClone) {
        return;
      }
      const newAddOn = cloneRateWithNewIds(addOnToClone, `${addOnToClone.name} (${t("common", "copy")})`);
      insert(index + 1, newAddOn);
      scrollAndFocusAddOn(index + 1);
    },
    [form, insert, scrollAndFocusAddOn, t]
  );

  const moveAddOnUp = useCallback(
    (index: number) => {
      if (index > 0) {
        move(index, index - 1);
      }
    },
    [move]
  );

  const moveAddOnDown = useCallback(
    (index: number) => {
      const length = (form.getValues("rates") ?? []).length;
      if (index < length - 1) {
        move(index, index + 1);
      }
    },
    [form, move]
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const formAddOns = form.getValues("rates") ?? [];
      const oldIndex = formAddOns.findIndex((addOn) => addOn.id === active.id);
      const newIndex = formAddOns.findIndex((addOn) => addOn.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        move(oldIndex, newIndex);
      }
    },
    [form, move]
  );

  const list = (
    <Stack>
      <Stack>
        {addOns.map((addOn, index) => {
          const initialAddOn = (form.getValues("rates") ?? [])[index] as RateData | undefined;
          return (
            <React.Fragment key={addOn.id}>
              <Collapse in appear={false} timeout={300} easing="ease-in-out">
                <AddOnCard
                  rateId={addOn.id ?? ""}
                  rateIndex={index}
                  alreadyBoughtTicketsCount={initialAddOn?.alreadyBoughtTicketsCount ?? 0}
                  isEditing={editOrCreateMode === "edit"}
                  totalCardNumber={addOns.length}
                  onDelete={deleteAddOn}
                  onDuplicate={duplicateAddOn}
                  onMoveUp={moveAddOnUp}
                  onMoveDown={moveAddOnDown}
                />
              </Collapse>
              {index < addOns.length - 1 && <AddDivider onClick={() => addAddOn(index + 1)} />}
            </React.Fragment>
          );
        })}
      </Stack>

      <Stack direction="row" justifyContent={isSmallScreen ? "center" : "flex-start"} marginTop={addOns.length ? 2 : 0}>
        <DashboardButton
          variant="outlined"
          vibe="brand"
          onClick={() => addAddOn()}
          startIcon={<Add />}
          data-test="add-add-on-button"
        >
          {t("dashboard", "ticketing.addOns.addButton")}
        </DashboardButton>
      </Stack>
    </Stack>
  );

  const listMaybeDnd = isSmallScreen ? (
    list
  ) : (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={addOns.map((addOn) => addOn.id)} strategy={verticalListSortingStrategy}>
        {list}
      </SortableContext>
    </DndContext>
  );

  return (
    <ControlledRatesExpansionProvider
      expandedRateId={expandedRateId}
      setExpandedRateId={setExpandedRateId}
      toggleExpanded={toggleExpanded}
    >
      <RateOptionHighlightProvider>{listMaybeDnd}</RateOptionHighlightProvider>
    </ControlledRatesExpansionProvider>
  );
};
